/**
 * Safe date handling for API timestamps.
 *
 * D1 rows carry TWO timestamp formats: ISO-8601 ("2026-07-14T18:07:46.790Z",
 * written by JS code) and SQLite's space-separated datetime('now') form
 * ("2026-07-14 18:07:46.790") — which IS UTC but carries no marker. The
 * space form is a browser landmine: Chrome parses it as LOCAL time (hours
 * off for any non-UTC viewer), Safari parses it as Invalid Date. date-fns
 * `format()` / `formatDistanceToNow()` THROW `RangeError: Invalid time
 * value` on an Invalid Date, which unmounts the whole route into the router
 * error boundary.
 *
 * That exact chain took down campaign creation for a real signup on
 * 2026-07-14: the wizard succeeded, then the campaign page rendered
 * `format(new Date(campaign.created_at))` on Safari and crashed to
 * "Unexpected Application Error! Invalid time value".
 *
 * Rules:
 *  - Never call `new Date(<api string>)` in a component — use parseApiDate.
 *  - Never hand a raw API string to a throwing formatter — use fmtDate /
 *    fmtDistanceToNow / fmtLocale, which render a placeholder instead of
 *    throwing.
 */
import { format as dateFnsFormat, formatDistanceToNow as dateFnsDistanceToNow } from 'date-fns'

/** Parse an API timestamp. Space-format SQLite timestamps are UTC. Returns null for anything unparseable. */
export function parseApiDate(value: string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null
  let d: Date
  if (typeof value === 'number') {
    d = new Date(value)
  } else {
    const m = value.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?)$/)
    d = new Date(m ? `${m[1]}T${m[2]}Z` : value)
  }
  return Number.isNaN(d.getTime()) ? null : d
}

/** Epoch millis for sorting; unparseable values sort to the epoch start instead of NaN-scrambling the comparator. */
export function apiDateMs(value: string | number | null | undefined): number {
  return parseApiDate(value)?.getTime() ?? 0
}

/** date-fns format() that renders a fallback instead of throwing. */
export function fmtDate(
  value: string | number | null | undefined,
  formatStr: string,
  fallback = '—',
): string {
  const d = parseApiDate(value)
  return d ? dateFnsFormat(d, formatStr) : fallback
}

/** date-fns formatDistanceToNow() that renders a fallback instead of throwing. */
export function fmtDistanceToNow(
  value: string | number | null | undefined,
  options?: { addSuffix?: boolean },
  fallback = '—',
): string {
  const d = parseApiDate(value)
  return d ? dateFnsDistanceToNow(d, options) : fallback
}

/** toLocale{Date,Time,DateTime}String that renders a fallback instead of "Invalid Date". */
export function fmtLocale(
  value: string | number | null | undefined,
  kind: 'date' | 'time' | 'datetime' = 'datetime',
  fallback = '—',
): string {
  const d = parseApiDate(value)
  if (!d) return fallback
  if (kind === 'date') return d.toLocaleDateString()
  if (kind === 'time') return d.toLocaleTimeString()
  return d.toLocaleString()
}
